import React from 'react';
import PropTypes from 'prop-types';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { feature } from 'topojson-client';
import { geoCentroid } from 'd3-geo';

import Counter from '../../../01_atoms/Counter';

class SingleCountryMap extends React.Component {
  countryStyleNeighbor = {
    fill: '#e7e4d2',
    stroke: '#e7e4d2',
    strokeWidth: 0.25,
    outline: 'none',
  };

  countryStyleHighlight = {
    fill: '#007b75',
    stroke: '#007b75',
    strokeWidth: 0.25,
    outline: 'none',
  };

  constructor(props) {
    super(props);

    this.state = { mapData: [] };
  }

  componentDidMount() {
    // Load geo data for all countries (world map).
    import('../../../../utils/world-50m.json').then((json) => {
      const world = json && json.default ? json.default : json;
      if (world && world.arcs) {
        const mapData = feature(world, world.objects[Object.keys(world.objects)[0]]).features;
        this.setState({ mapData });
      }
    });
  }

  render() {
    const { country, timestamp, scale = 320 } = this.props;
    const { mapData } = this.state;

    // only use map data of highlighted country and given neighbors for efficiency
    const relevantMapData = mapData.filter(
      (geography) =>
        country.code === geography.id ||
        country.neighbors.map((neighbor) => neighbor.code).includes(geography.id),
    );

    // calculate # full (elapsed) months from given timestamp
    // or # full years, rounded down
    let diff = Date.now() / 1000 - timestamp;
    diff /= 60 * 60 * 24 * 30.5;

    let amount = Math.abs(Math.ceil(diff));
    let unit = 'month';

    if (amount > 11) {
      amount = Math.floor(amount / 12);
      unit = 'year';
    }

    if (amount !== 1 && amount !== 12) {
      unit += 's';
    }

    return (
      <div className="container">
        <div className="box">
          <p>
            Time spent in <strong>{country.name}</strong>
          </p>

          <div className="d-flex">
            <Counter value={amount} />

            <div className="header-two align-self-start">{unit}</div>
          </div>

          <ComposableMap projectionConfig={{ scale }} width={280} height={230}>
            <ZoomableGroup
              center={geoCentroid(
                relevantMapData.filter((geography) => country.code === geography.id)[0],
              )}
              disablePanning
            >
              <Geographies geography={relevantMapData}>
                {({ geographies }) =>
                  geographies.map((geography) => (
                    <g key={`geography-${geography.id}`}>
                      <Geography
                        geography={geography}
                        style={
                          country.code === geography.id
                            ? {
                                default: this.countryStyleHighlight,
                                hover: this.countryStyleHighlight,
                                pressed: this.countryStyleHighlight,
                              }
                            : {
                                default: this.countryStyleNeighbor,
                                hover: this.countryStyleNeighbor,
                                pressed: this.countryStyleNeighbor,
                              }
                        }
                      />
                    </g>
                  ))
                }
              </Geographies>

              {/* Neighboring country labels */}
              <>
                {relevantMapData.map(
                  (geography) =>
                    geography.id !== country.code && (
                      <Marker
                        key={`label-${geography.id}`}
                        coordinates={geoCentroid(geography)}
                        tabable={false}
                      >
                        <text
                          y="7"
                          textAnchor="middle"
                          style={{
                            textTransform: 'uppercase',
                            color: '#212431',
                            fontSize: '14px',
                            userSelect: 'none',
                          }}
                        >
                          {
                            country.neighbors.filter(
                              (neighbor) => neighbor.code === geography.id,
                            )[0].name
                          }
                        </text>
                      </Marker>
                    ),
                )}
              </>
            </ZoomableGroup>
          </ComposableMap>
        </div>
      </div>
    );
  }
}

SingleCountryMap.propTypes = {
  country: PropTypes.shape({
    name: PropTypes.string,
    code: PropTypes.string,
    neighbors: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        code: PropTypes.string,
      }),
    ),
  }).isRequired,
  scale: PropTypes.number,
  timestamp: PropTypes.number.isRequired,
};

export default SingleCountryMap;
